import React, { useState, useMemo, useEffect } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import { AdvancedLifeAnalysisResult, LifeThemeId, LifeThemeQuestion } from '../../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from '../../utils/advancedLifeAnalysisTaxonomy';
import { runAdvancedLifeAnalysis } from '../../utils/advancedLifeAnalysisEngine';
import { getNatalPromiseTier, getTimingConfluence, calculateProjectedAgeWindow } from '../../utils/presentationHelper';
import { NorthIndianSvgChart, SouthIndianSvgChart } from '../ChartViewer';
import { ResponsiveChartContainer } from '../ResponsiveChartContainer';
import { BasicLifeQuestionsModal } from './BasicLifeQuestionsModal';
import { BasicFiveRuleAssessmentCard } from './BasicFiveRuleAssessmentCard';
import { HarmonizedRemediesHub } from './HarmonizedRemediesHub';
import { CurrentPhaseForecastView } from '../CurrentPhaseForecastView';
import { TemporalWindowCard } from '../v518/TemporalWindowCard';
import { GeminiAstrologer } from '../GeminiAstrologer';
import {
  LAGNA_INSIGHTS,
  HOUSE_PLAIN_MEANINGS,
  getAuspiciousDirection,
  getDetailedHouseSynthesis
} from '../../utils/basicProfileInterpretations';
import { getZodiacName } from '../../utils/language';
import { calculateNumerology } from '../../utils/numerologyEngine';
import { getColorTaxonomy } from '../AuspiciousEssentialsBar';
import { formatDate, formatDOB, formatTimeToAMPM, calculateAgeString } from '../../utils/dateUtils';
import {
  getNativeAgeInYears,
  getDeshKaalPatraThemes,
  getDeshKaalPatraQuestions,
  getDeshKaalPatraThemeDisplay
} from '../../utils/deshKaalPatraFilter';

import {
  Compass,
  Sparkles,
  Zap,
  CheckCircle2,
  Calendar,
  Layers,
  Briefcase,
  Heart,
  Coins,
  Home,
  Car,
  Baby,
  GraduationCap,
  Activity,
  TrendingUp,
  Globe,
  Scale,
  Users,
  Maximize2,
  Gem,
  Hash,
  Palette,
  Sun,
  Moon,
  Clock,
  ChevronDown,
  ChevronUp,
  Info,
  Edit3,
  MapPin,
  ChevronRight,
  HelpCircle,
  X,
  MessageSquare,
  Award,
  Hourglass
} from 'lucide-react';

interface BasicAnalysisViewProps {
  kundali: CompleteKundali;
  analysisResult?: AdvancedLifeAnalysisResult;
  selectedThemeId?: LifeThemeId;
  onSelectTheme?: (id: LifeThemeId) => void;
  selectedQuestionId?: string;
  onSelectQuestion?: (id: string) => void;
  lang?: Language;
  chartStyle?: ChartStyle;
  setChartStyle?: (style: ChartStyle) => void;
  onOpenPrintReport?: (mode?: 'BASIC' | 'LEARNER' | 'PRO' | 'ACHARYA') => void;
  onSwitchRole?: (role: 'PRO') => void;
  onOpenAcharya?: (question?: string) => void;
  onEditKundali?: () => void;
  onOpenBTR?: () => void;
  isBasicUser?: boolean;
}

const THEME_ICONS: Record<LifeThemeId, React.ComponentType<{ className?: string }>> = {
  career_job: Briefcase,
  marriage_relationship: Heart,
  wealth_finance: Coins,
  house_property: Home,
  vehicle: Car,
  children: Baby,
  education: GraduationCap,
  health_vitality: Activity,
  business: TrendingUp,
  foreign_travel: Globe,
  litigation_competition: Scale,
  spirituality: Sparkles,
  family_parents: Users
};

export const BasicAnalysisView: React.FC<BasicAnalysisViewProps> = ({
  kundali,
  analysisResult,
  selectedThemeId,
  onSelectTheme,
  selectedQuestionId,
  onSelectQuestion,
  lang = 'en',
  chartStyle = 'north',
  setChartStyle,
  onOpenPrintReport,
  onSwitchRole,
  onOpenAcharya,
  onEditKundali,
  onOpenBTR,
  isBasicUser = false
}) => {
  const isHi = lang === 'hi';
  const allThemes = Object.values(LIFE_THEMES_REGISTRY);

  const [internalThemeId, setInternalThemeId] = useState<LifeThemeId>('career_job');
  const [internalQuestionId, setInternalQuestionId] = useState<string>('q_career_timing');

  const effectiveThemeId = selectedThemeId || internalThemeId;
  const effectiveQuestionId = selectedQuestionId || internalQuestionId;

  // Desh-Kaal-Patra filtered themes and questions
  const nativeAge = useMemo(() => getNativeAgeInYears(kundali), [kundali]);
  const ageAppropriateThemes = useMemo(() => getDeshKaalPatraThemes(kundali, allThemes), [kundali, allThemes]);

  // Current active theme conforming to Desh-Kaal-Patra
  const currentTheme = useMemo(() => {
    return ageAppropriateThemes.find((t) => t.id === effectiveThemeId) || ageAppropriateThemes[0] || allThemes[0];
  }, [ageAppropriateThemes, effectiveThemeId, allThemes]);

  // Questions for current theme filtered by Desh-Kaal-Patra
  const activeThemeQuestions = useMemo(() => {
    return getDeshKaalPatraQuestions(kundali, currentTheme);
  }, [kundali, currentTheme]);

  // Active question ensuring age appropriateness
  const currentQuestion = useMemo(() => {
    return activeThemeQuestions.find((q) => q.id === effectiveQuestionId) || activeThemeQuestions[0];
  }, [activeThemeQuestions, effectiveQuestionId]);

  // Sync state if selectedTheme or selectedQuestion was out-of-sync
  useEffect(() => {
    if (currentTheme.id !== effectiveThemeId) {
      if (onSelectTheme) onSelectTheme(currentTheme.id);
      else setInternalThemeId(currentTheme.id);
    }
    if (currentQuestion && currentQuestion.id !== effectiveQuestionId) {
      if (onSelectQuestion) onSelectQuestion(currentQuestion.id);
      else setInternalQuestionId(currentQuestion.id);
    }
  }, [currentTheme, currentQuestion, effectiveThemeId, effectiveQuestionId, onSelectTheme, onSelectQuestion]);

  // Interactive UI states
  const [isLifeQuestionsModalOpen, setIsLifeQuestionsModalOpen] = useState<boolean>(false);
  const [selectedHouse, setSelectedHouse] = useState<number>(1);
  const [isChartExplorerOpen, setIsChartExplorerOpen] = useState<boolean>(true);
  const [isStageContextExpanded, setIsStageContextExpanded] = useState<boolean>(false);
  const [isAIAstrologerOpen, setIsAIAstrologerOpen] = useState<boolean>(false);
  const [aiAstrologerInitialPrompt, setAiAstrologerInitialPrompt] = useState<string>('');

  // Dynamically compute active analysis result based on selected question and theme to honor Desh-Kaal-Patra
  const activeAnalysisResult = useMemo(() => {
    try {
      return runAdvancedLifeAnalysis(kundali, currentTheme.id, currentQuestion?.id || '');
    } catch {
      return analysisResult;
    }
  }, [kundali, currentTheme.id, currentQuestion, analysisResult]);

  // Destructure Kundali elements
  const { houses, planets, gemstones, dashas } = kundali;
  const lagnaHouse = houses[0];
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const moonPlanet = planets.find((p) => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Taurus';

  // Lagna insights (Archetype, nature, advice, auspicious parameters)
  const lagnaData = LAGNA_INSIGHTS[lagnaSign] || LAGNA_INSIGHTS.Aries;
  const lagnaContent = isHi ? lagnaData.hi : lagnaData.en;

  // Active running Dasha
  const currentMD = dashas.find((d) => d.isCurrent);
  const currentAD = currentMD?.antardashas?.find((ad) => ad.isCurrent);

  // Primary Life Gemstone
  const lifeGem = gemstones?.find((g) => g.type === 'Life Stone (Lagna)') || gemstones?.[0] || {
    gemstone: 'Yellow Sapphire / Pukhraj',
    substitute: 'Yellow Topaz',
    finger: 'Index Finger',
    metal: 'Gold / Brass',
    wearingDay: 'Thursday morning'
  };

  // Numerology Analysis (Driver & Destiny Numbers)
  const numerology = useMemo(() => {
    return calculateNumerology(
      kundali.birthDetails?.date || '1990-01-01',
      kundali.birthDetails?.name || 'Jataka',
      kundali.birthDetails?.gender || 'male'
    );
  }, [kundali.birthDetails]);

  // Color Taxonomy (Lucky, Neutral & Avoid Colors)
  const colorTaxonomy = useMemo(() => {
    return getColorTaxonomy(lagnaSign, numerology.moolank, lang, lagnaContent.luckyColor);
  }, [lagnaSign, numerology.moolank, lang, lagnaContent.luckyColor]);

  // Detailed Synthesis for Selected House
  const detailedHouseSynthesis = useMemo(() => {
    const hInfo = houses.find((h) => h.houseNumber === selectedHouse);
    const sign = hInfo?.sign || 'Aries';
    const signLord = hInfo?.signLord || 'Mars';
    const lordPlanet = planets.find((p) => p.name === signLord);
    const signLordHouse = lordPlanet?.house || selectedHouse;
    const signLordSign = lordPlanet?.sign || sign;
    const occupying = planets
      .filter((p) => p.house === selectedHouse && p.name !== 'Ascendant')
      .map((p) => ({
        name: p.name,
        dignity: p.dignity,
        isRetrograde: p.isRetrograde,
        isCombust: p.isCombust,
        nakshatra: p.nakshatra
      }));

    return getDetailedHouseSynthesis(
      selectedHouse,
      sign,
      signLord,
      signLordHouse,
      signLordSign,
      occupying
    );
  }, [houses, planets, selectedHouse]);

  // Selected house details for explorer fallback
  const selectedHouseInfo = isHi ? HOUSE_PLAIN_MEANINGS[selectedHouse]?.hi : HOUSE_PLAIN_MEANINGS[selectedHouse]?.en;
  const selectedHouseSign = houses.find((h) => h.houseNumber === selectedHouse)?.sign || '';
  const selectedHousePlanets = planets.filter((p) => p.house === selectedHouse);

  // Life Analysis Result destructuring from dynamic activeAnalysisResult
  const {
    natalPromise,
    phalaBhutaWindows,
    lifeStageContext,
    supportingFactors,
    gocharAnalysis,
    karakaAssessments
  } = activeAnalysisResult;
  const primaryWindow = phalaBhutaWindows.primary;

  const promiseTier = getNatalPromiseTier(natalPromise.score, lang);
  const timingConfluence = getTimingConfluence(primaryWindow.confidenceScore, lang);
  const projectedAgeStr = calculateProjectedAgeWindow(
    kundali.birthDetails?.date || '1990-05-15',
    primaryWindow.startDate,
    primaryWindow.endDate,
    lang
  );

  // Natural Language Supportive Explanations
  const houseActivationText = primaryWindow?.activatedHouses && primaryWindow.activatedHouses.length > 0
    ? isHi
      ? `संबद्ध जीवन क्षेत्रों (भाव ${primaryWindow.activatedHouses.join(', ')}) में अनुकूल ऊर्जा एवं सक्रिय सहयोग`
      : `Supportive alignment across key life areas with harmonious energy`
    : isHi
      ? 'संबद्ध प्रमुख जीवन क्षेत्रों की अनुकूल स्थिति'
      : 'Harmonious configuration across key relevant life areas';

  const planetarySupportText = karakaAssessments && karakaAssessments.length > 0
    ? isHi
      ? `${karakaAssessments.map((k) => k.name).slice(0, 2).join(' व ')} जैसे प्रमुख मार्गदर्शक ग्रहों का सकारात्मक सहयोग`
      : `Supportive placement and strength of key guiding planets (${karakaAssessments.map((k) => k.name).slice(0, 2).join(', ')})`
    : isHi
      ? 'मार्गदर्शक ग्रहों का सकारात्मक सहयोग'
      : 'Positive supportive guidance from key planetary factors';

  const dashaSupportText = primaryWindow
    ? isHi
      ? `${primaryWindow.mahadashaLord} महादशा एवं ${primaryWindow.antardashaLord} अंतर्दशा का पारस्परिक अनुकूल योग`
      : `Supportive harmony between ${primaryWindow.mahadashaLord} major period and ${primaryWindow.antardashaLord} sub-period`
    : isHi
      ? 'वर्तमान समय का शुभ फल-प्रदाता संबंध'
      : 'Active time periods create a favorable confluence';

  const transitSupportText = gocharAnalysis
    ? isHi
      ? gocharAnalysis.doubleTransitActive
        ? 'बृहस्पति एवं शनि का शुभ प्रभाव इस जीवन क्षेत्र को अनुकूलता प्रदान कर रहा है'
        : 'गोचर में प्रमुख ग्रहों का सकारात्मक संचार'
      : gocharAnalysis.doubleTransitActive
        ? 'Supportive planetary movements currently bringing favorable focus to this area'
        : 'Favorable planetary movements aligning with key life areas'
    : isHi
      ? 'गोचरीय ग्रहों का सकारात्मक संमिलन'
      : 'Favorable planetary alignment across key areas';

  const activeThemeDisplay = getDeshKaalPatraThemeDisplay(currentTheme, nativeAge, lang);

  const profileHeaderData = useMemo(() => ({
    name: kundali.birthDetails?.name || (isHi ? 'जातक कुंडली' : 'Native Horoscope'),
    lagna: getZodiacName(lagnaSign, lang),
    moonSign: getZodiacName(moonSign, lang),
    birthDate: formatDOB(kundali.birthDetails?.date),
    birthTime: formatTimeToAMPM(kundali.birthDetails?.time),
    age: calculateAgeString(kundali.birthDetails?.date, lang) || (lifeStageContext ? `${lifeStageContext.years}y ${lifeStageContext.months}m` : 'N/A'),
    birthPlace: kundali.birthDetails?.placeName || 'N/A',
    activeDasha: currentMD && currentAD ? `${currentMD.planet}-${currentAD.planet}` : 'N/A'
  }), [kundali.birthDetails, isHi, lagnaSign, lang, moonSign, lifeStageContext, currentMD, currentAD]);

  return (
    <div
      id="basic-presentation-view"
      className="space-y-8 animate-fade-in text-astro-text"
      role="tabpanel"
      aria-labelledby="role-tab-basic"
    >
      {/* ========================================================================= */}
      {/* 1. NATIVE INTEGRATED DESH-KAAL-PATRA LIFE STAGE CONTEXT                  */}
      {/* ========================================================================= */}
      <section id="basic-core-essence-section" className="space-y-3">
        {/* Integrated Life Stage Context & Desh-Kaal-Patra Strip (Compact Resized) */}
        {lifeStageContext && (
          <div className="p-2.5 sm:p-3 rounded-xl bg-[#FAF6ED] border border-astro-accent/30 space-y-2 mb-3 shadow-2xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md bg-astro-primary text-white tracking-wider">
                    {isHi ? 'जीवन अवस्था एवं देश-काल-पात्र' : 'Life Stage Context (Desh-Kaal-Patra)'}
                  </span>
                  <span className="text-xs font-bold text-astro-primary">
                    {isHi ? lifeStageContext.stage.labelHi : lifeStageContext.stage.labelEn} ({isHi ? lifeStageContext.stage.subLabelHi : lifeStageContext.stage.subLabelEn})
                  </span>
                  <span className="text-[11px] font-bold text-astro-accent">
                    • {isHi ? 'प्रमुख पुरुषार्थ:' : 'Primary Pillar:'} {lifeStageContext.stage.primaryPurushartha}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setIsStageContextExpanded(!isStageContextExpanded)}
                  className="text-xs font-bold text-astro-primary hover:text-astro-accent flex items-center gap-1 self-start sm:self-auto cursor-pointer transition-colors"
                >
                  <span>{isStageContextExpanded ? (isHi ? 'संक्षिप्त करें' : 'Less Details') : (isHi ? 'आयु व ग्रह परिपक्वता संदर्भ' : 'Stage & Maturation Details')}</span>
                  {isStageContextExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>

              <p className="text-xs text-astro-text leading-relaxed">
                {isHi ? lifeStageContext.deshKaalPatraGuidanceHi : lifeStageContext.deshKaalPatraGuidanceEn}
              </p>

              {/* Expandable Maturation & Developmental Overview */}
              {isStageContextExpanded && (
                <div className="pt-3 border-t border-astro-border space-y-3 animate-fade-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                      <span className="text-[10px] font-bold uppercase text-astro-primary block">
                        {isHi ? 'विकासशील केंद्र एवं प्रासंगिक भाव:' : 'Developmental Focus & Active Houses:'}
                      </span>
                      <p className="text-xs text-astro-secondary-text leading-relaxed">
                        {isHi ? lifeStageContext.stage.developmentalFocusHi : lifeStageContext.stage.developmentalFocusEn}
                      </p>
                    </div>

                    <div className="p-3 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                      <span className="text-[10px] font-bold uppercase text-astro-primary block">
                        {isHi ? 'दशा व गोचर समन्वय:' : 'Active Dasha & Transit Confluence:'}
                      </span>
                      <p className="text-xs text-astro-secondary-text leading-relaxed">
                        {isHi ? lifeStageContext.activeDashaContext.dashaThemeHi : lifeStageContext.activeDashaContext.dashaThemeEn}
                      </p>
                    </div>
                  </div>

                  {/* Planetary Maturation Status Grid */}
                  {lifeStageContext.maturationStatuses && lifeStageContext.maturationStatuses.length > 0 && (
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block mb-1.5">
                        {isHi ? 'शास्त्रीय ग्रह परिपक्वता चक्र (पराशर मत):' : 'Classical Planetary Maturation Ages (Parashari):'}
                      </span>
                      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-1.5 text-center">
                        {lifeStageContext.maturationStatuses.map((mat, idx) => (
                          <div
                            key={idx}
                            className={`p-1.5 rounded-lg border text-[11px] ${
                              mat.isMatured
                                ? 'bg-astro-primary/10 border-astro-primary/30 text-astro-primary'
                                : 'bg-astro-bg border-astro-border text-astro-secondary-text'
                            }`}
                          >
                            <div className="font-bold truncate">{mat.planet}</div>
                            <div className="text-[10px] opacity-80">{mat.naturalMaturityAge}y</div>
                            <div className="text-[9px] font-extrabold uppercase mt-0.5">
                              {mat.isMatured ? '✓' : '•'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


        {/* Personality Archetype & Auspicious Keys */}
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs space-y-5">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-astro-border/80 pb-4">
            <div className="space-y-1.5 max-w-3xl">
              <span className="text-[11px] uppercase tracking-wider font-bold text-astro-accent bg-[#FAF6ED] border border-astro-border px-2.5 py-0.5 rounded-md inline-block">
                {lagnaContent.archetype}
              </span>
              <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed pt-1">
                {lagnaContent.nature}
              </p>
            </div>

            <div className="w-full lg:w-auto bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-border shrink-0 space-y-1.5">
              <span className="text-[10px] uppercase font-bold text-astro-primary tracking-wider block">
                {isHi ? 'आपकी स्वाभाविक विशेषताएं व आंतरिक बल:' : 'Your Natural Strengths & Qualities:'}
              </span>
              <ul className="space-y-1 text-xs text-astro-text">
                {lagnaContent.strengths.map((str, idx) => (
                  <li key={idx} className="flex items-center gap-1.5 font-medium">
                    <span className="text-astro-accent">✓</span>
                    <span>{str}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Auspicious Keys Grid */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text mb-3">
              {isHi ? 'आपके व्यक्तिगत शुभ सूत्र व मार्गदर्शक तत्व:' : 'Your Personal Guiding Elements & Auspicious Keys:'}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'gemstones' }))}
                className="bg-[#FAF6ED] hover:bg-amber-50/80 p-3.5 rounded-xl border border-astro-border hover:border-astro-accent/50 space-y-1 text-left transition-all cursor-pointer shadow-2xs hover:shadow-xs group"
                title={isHi ? 'विस्तृत रत्न परामर्श देखें' : 'View Full Gemstone Advisor'}
              >
                <div className="text-[10px] font-bold uppercase tracking-wider text-astro-accent flex items-center justify-between gap-1">
                  <span className="flex items-center gap-1">
                    <Gem className="w-3.5 h-3.5 text-astro-accent group-hover:scale-110 transition-transform" />
                    {isHi ? 'भाग्य रत्न' : 'Auspicious Gem'}
                  </span>
                  <span className="text-[9px] text-astro-accent font-semibold underline opacity-0 group-hover:opacity-100 transition-opacity">
                    {isHi ? 'देखें' : 'View'}
                  </span>
                </div>
                <div className="text-xs font-bold text-astro-primary truncate">{lifeGem.gemstone}</div>
                <div className="text-[10px] text-astro-secondary-text">{lifeGem.metal || 'Gold / Brass'}</div>
              </button>

              {/* Lucky Numbers (शुभ अंक) */}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'))}
                className="bg-[#FAF6ED] hover:bg-amber-100/70 p-3.5 rounded-xl border border-amber-300/80 hover:border-amber-500 space-y-1 text-left transition-all cursor-pointer shadow-2xs hover:shadow-xs group"
                title={isHi ? 'विस्तृत शुभ अंक व रंग देखें' : 'View Lucky Numbers & Colors Guide'}
              >
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center justify-between gap-1">
                  <span className="flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5 text-amber-600 group-hover:scale-110 transition-transform" />
                    {isHi ? 'शुभ अंक' : 'Lucky Numbers'}
                  </span>
                  <span className="text-[9px] text-amber-600 font-semibold underline opacity-0 group-hover:opacity-100 transition-opacity">
                    {isHi ? 'देखें' : 'View'}
                  </span>
                </div>
                <div className="text-xs font-bold text-amber-800 dark:text-amber-300 truncate">
                  {isHi ? `मूलांक ${numerology.moolank} / भाग्यांक ${numerology.bhagyank}` : `Driver ${numerology.moolank} / Path ${numerology.bhagyank}`}
                </div>
                <div className="text-[10px] text-astro-secondary-text truncate">
                  {isHi ? `मित्र: ${numerology.luckyNumbers.join(', ')}` : `Friendly: ${numerology.luckyNumbers.join(', ')}`}
                </div>
              </button>

              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'))}
                className="bg-[#FAF6ED] hover:bg-rose-50/80 p-3.5 rounded-xl border border-astro-border hover:border-rose-400 space-y-1 text-left transition-all cursor-pointer shadow-2xs group"
                title={isHi ? 'विस्तृत शुभ, तटस्थ व वर्जित रंग विवरण देखें' : 'View Lucky, Neutral & Avoid Colors Guide'}
              >
                <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 flex items-center justify-between gap-1">
                  <span className="flex items-center gap-1">
                    <Palette className="w-3.5 h-3.5 text-rose-600 group-hover:scale-110 transition-transform" />
                    {isHi ? 'रंग मार्गदर्शन' : 'Color Harmony'}
                  </span>
                  <span className="text-[9px] text-rose-600 font-semibold underline opacity-0 group-hover:opacity-100 transition-opacity">
                    {isHi ? 'देखें' : 'View'}
                  </span>
                </div>
                <div className="text-xs font-bold text-astro-text truncate">
                  <span className="text-emerald-700 dark:text-emerald-400 font-bold">{isHi ? 'शुभ: ' : 'Lucky: '}</span>
                  {colorTaxonomy.lucky.summary}
                </div>
                <div className="text-[10px] text-astro-secondary-text truncate">
                  <span className="text-rose-700 dark:text-rose-400 font-semibold">{isHi ? 'वर्जित: ' : 'Avoid: '}</span>
                  {colorTaxonomy.avoid.summary}
                </div>
              </button>

              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-border space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-astro-primary" />
                  {isHi ? 'अनुकूल दिन' : 'Favorable Day'}
                </div>
                <div className="text-xs font-bold text-astro-primary truncate">{lagnaContent.luckyDay}</div>
                <div className="text-[10px] text-astro-secondary-text">{isHi ? 'महत्वपूर्ण कार्यों हेतु' : 'For key milestones'}</div>
              </div>

              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-border space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-astro-accent flex items-center gap-1">
                  <Compass className="w-3.5 h-3.5 text-astro-accent" />
                  {isHi ? 'शुभ दिशा' : 'Lucky Direction'}
                </div>
                <div className="text-xs font-bold text-astro-text truncate">
                  {getAuspiciousDirection(lagnaSign, lang)}
                </div>
                <div className="text-[10px] text-astro-secondary-text">{isHi ? 'अध्ययन व कार्य' : 'Work & Study'}</div>
              </div>

              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-border space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1">
                  <Moon className="w-3.5 h-3.5 text-astro-primary" />
                  {isHi ? 'इष्ट देव' : 'Ruling Deva'}
                </div>
                <div className="text-xs font-bold text-astro-text truncate">{lagnaContent.deity.split('&')[0]}</div>
                <div className="text-[10px] text-astro-secondary-text">{isHi ? 'आशीर्वाद एवं शांति' : 'Peace & Alignment'}</div>
              </div>

              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-border space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-astro-primary" />
                  {isHi ? 'सक्रिय दशा' : 'Running Cycle'}
                </div>
                <div className="text-xs font-bold text-astro-accent truncate">
                  {currentMD?.planet} - {currentAD?.planet}
                </div>
                <div className="text-[10px] text-astro-secondary-text truncate">
                  {currentAD?.endDate ? formatDate(currentAD.endDate) : (isHi ? 'सक्रिय' : 'Active')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2. INTERACTIVE NATAL CHART & 12 HOUSES EXPLORER                            */}
      {/* ========================================================================= */}
      <section id="basic-chart-explorer-section" className="space-y-4">
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-astro-border pb-3">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-astro-primary" />
              <h3 className="text-sm sm:text-base font-bold font-serif text-astro-text">
                {isHi ? 'आपकी जन्म कुंडली एवं १२ भाव दर्शन' : 'Your Birth Chart & 12 Life Houses'}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setIsChartExplorerOpen(!isChartExplorerOpen)}
              className="text-xs font-bold text-astro-primary hover:text-astro-accent flex items-center gap-1 transition-colors cursor-pointer"
            >
              <span>{isChartExplorerOpen ? (isHi ? 'संक्षिप्त करें' : 'Collapse') : (isHi ? 'विस्तार करें' : 'Expand')}</span>
              {isChartExplorerOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {isChartExplorerOpen && (
            <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-5 items-start pt-1 animate-fade-in">
              {/* Dynamic Left Column (Chart Canvas - 7 Columns) */}
              <div className="lg:col-span-7 flex flex-col items-center w-full">
                <div className="w-full bg-astro-bg border border-astro-border rounded-2xl p-4 shadow-xs flex items-center justify-center">
                  {/* Responsive, aspect-ratio locked chart container */}
                  <div className="w-full max-h-[calc(100vh-160px)] aspect-square flex items-center justify-center">
                    <ResponsiveChartContainer maxDimension={1200} minDimension={240} className="w-full h-full flex items-center justify-center">
                      {() =>
                        chartStyle === 'north' ? (
                          <NorthIndianSvgChart
                            chartData={kundali.charts.D1}
                            planets={kundali.planets}
                            lang={lang}
                            selectedHouseNumber={selectedHouse}
                            onSelectHouse={(h) => setSelectedHouse(h)}
                          />
                        ) : (
                          <SouthIndianSvgChart
                            chartData={kundali.charts.D1}
                            planets={kundali.planets}
                            lang={lang}
                            selectedHouseNumber={selectedHouse}
                            onSelectHouse={(h) => setSelectedHouse(h)}
                          />
                        )
                      }
                    </ResponsiveChartContainer>
                  </div>
                </div>
              </div>

              {/* Dynamic Right Column (Dossier & Analysis - 5 Columns) */}
              <div className="lg:col-span-5 flex flex-col gap-4 w-full">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text block mb-2">
                    {isHi ? 'किसी भी भाव पर क्लिक करके अर्थ समझें:' : 'Click Any House to Explore Meaning:'}
                  </span>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((hNum) => {
                      const isSelected = selectedHouse === hNum;
                      return (
                        <button
                          key={hNum}
                          type="button"
                          onClick={() => setSelectedHouse(hNum)}
                          className={`p-2 rounded-lg text-xs font-bold text-center transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-astro-primary text-white shadow-2xs ring-2 ring-astro-accent/30'
                              : 'bg-[#FAF6ED] text-astro-text hover:bg-[#FAF6ED]/80 border border-astro-border'
                          }`}
                        >
                          {isHi ? `${hNum} भाव` : `H${hNum}`}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selected House Detailed Insight */}
                <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-astro-primary">
                        {isHi
                          ? `${detailedHouseSynthesis.taxonomy.titleHi} • ${detailedHouseSynthesis.taxonomy.sanskritNameHi}`
                          : `${detailedHouseSynthesis.taxonomy.titleEn} • ${detailedHouseSynthesis.taxonomy.sanskritName}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] font-bold">
                      <span className="text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded border border-astro-primary/20">
                        {getZodiacName(selectedHouseSign, lang)}
                      </span>
                      <span className="text-astro-accent bg-[#FAF6ED] px-2 py-0.5 rounded border border-astro-accent/40">
                        {isHi ? `स्वामी: ${detailedHouseSynthesis.signLord}` : `Lord: ${detailedHouseSynthesis.signLord}`}
                      </span>
                    </div>
                  </div>

                  {/* Core Karakatvas & Keywords */}
                  <div className="text-[11px] text-astro-secondary-text flex flex-wrap items-center gap-1.5 bg-astro-bg p-2 rounded-lg border border-astro-border">
                    <span className="font-bold text-astro-primary">
                      {isHi ? 'कारक तत्व:' : 'Key Significations:'}
                    </span>
                    {(isHi ? detailedHouseSynthesis.taxonomy.coreSignificationsHi : detailedHouseSynthesis.taxonomy.coreSignificationsEn).map((sig, sIdx) => (
                      <span key={sIdx} className="bg-[#FAF6ED] px-1.5 py-0.5 rounded border border-astro-border text-[10px] text-astro-text">
                        {sig}
                      </span>
                    ))}
                    <span className="ml-auto text-[10px] text-astro-accent font-bold">
                      {isHi
                        ? `स्थिर कारक: ${detailedHouseSynthesis.taxonomy.karakasHi.join(', ')}`
                        : `Natural Karakas: ${detailedHouseSynthesis.taxonomy.karakasEn.join(', ')}`}
                    </span>
                  </div>

                  {/* Lord Placement & Synthesis */}
                  <div className="text-xs text-astro-text space-y-1.5">
                    <p className="leading-relaxed">
                      {isHi ? detailedHouseSynthesis.lordPlacementAnalysisHi : detailedHouseSynthesis.lordPlacementAnalysisEn}
                    </p>
                    <p className="leading-relaxed text-astro-secondary-text">
                      {isHi ? detailedHouseSynthesis.synthesisHi : detailedHouseSynthesis.synthesisEn}
                    </p>
                  </div>

                  {/* Practical Action Guidance */}
                  <div className="pt-2 border-t border-astro-border flex items-start gap-2 text-[11px]">
                    <span className="text-[10px] font-bold uppercase text-astro-primary bg-astro-primary/10 px-1.5 py-0.5 rounded shrink-0">
                      {isHi ? 'फलित सूत्र' : 'Synthesis'}
                    </span>
                    <span className="text-astro-text leading-relaxed">
                      {isHi ? detailedHouseSynthesis.actionGuidanceHi : detailedHouseSynthesis.actionGuidanceEn}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. UNIFIED CLASSICAL LIFE INQUIRIES & DYNAMIC TIMING ENGINE               */}
      {/* ========================================================================= */}
      <section id="basic-unified-life-inquiries-section" className="space-y-6">
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-7 shadow-xs space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-astro-primary/10 flex items-center justify-center text-astro-primary border border-astro-primary/20">
                <Compass className="w-5 h-5 text-astro-accent" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-astro-text font-serif">
                  {isHi ? 'जीवन प्रश्न एवं शास्त्रीय समाधान' : 'Life Questions & Classical Timing Insights'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'अपनी रुचि के किसी भी प्रश्न पर क्लिक करें और उसका विस्तृत शास्त्रीय उत्तर व समय तुरंत देखें।'
                    : 'Click any question below to immediately see its personalized classical astrological synthesis and timing.'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsLifeQuestionsModalOpen(true)}
              className="self-start sm:self-auto px-4 py-2 rounded-xl border border-astro-primary bg-[#FAF6ED] hover:bg-astro-bg text-astro-primary text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-2xs"
            >
              <Maximize2 className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'सभी ५०+ प्रश्न देखें' : 'Explore All 50+ Questions'}</span>
            </button>
          </div>

          {/* Desh-Kaal-Patra Filtered Life Domain Category Selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text">
                {isHi ? 'जीवन क्षेत्र चुनें (देश-काल-पात्र अनुकूल):' : 'Choose Life Domain (Desh-Kaal-Patra Adapted):'}
              </span>
              <span className="text-[11px] text-astro-primary font-semibold">
                {ageAppropriateThemes.length} {isHi ? 'उपयुक्त क्षेत्र' : 'Relevant Domains'}
              </span>
            </div>

            <div className="w-full overflow-x-auto custom-horizontal-scrollbar pb-2">
              <div className="flex items-center gap-2 min-w-max px-0.5">
                {ageAppropriateThemes.map((theme) => {
                  const Icon = THEME_ICONS[theme.id] || Sparkles;
                  const isSelected = currentTheme.id === theme.id;
                  const display = getDeshKaalPatraThemeDisplay(theme, nativeAge, lang);

                  return (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => {
                        onSelectTheme(theme.id);
                        const availableQuestions = getDeshKaalPatraQuestions(kundali, theme);
                        const firstQ = availableQuestions[0]?.id;
                        if (firstQ) onSelectQuestion(firstQ);
                      }}
                      className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all min-h-[44px] touch-manipulation cursor-pointer ${
                        isSelected
                          ? 'bg-astro-primary text-white border-astro-primary shadow-2xs ring-2 ring-astro-accent/30 font-bold'
                          : 'bg-[#FAF6ED] border-astro-border text-astro-text hover:border-astro-accent'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                      <span>{display.name.split(',')[0]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Age-Appropriate Curated Questions for Active Domain */}
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text block mb-2">
              {isHi ? 'प्रश्न चुनें (क्लिक करने पर नीचे विस्तृत उत्तर देखें):' : 'Select Inquiry (Click to view dynamic answer below):'}
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {activeThemeQuestions.map((q: LifeThemeQuestion) => {
                const isSelected = currentQuestion?.id === q.id;

                return (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => onSelectQuestion(q.id)}
                    className={`p-3.5 rounded-xl border text-left text-xs transition-all min-h-[48px] flex items-start gap-2.5 touch-manipulation cursor-pointer ${
                      isSelected
                        ? 'bg-[#FAF6ED] border-astro-primary shadow-2xs ring-2 ring-astro-primary/20 font-bold text-astro-primary'
                        : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent'
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${isSelected ? 'bg-astro-primary' : 'bg-astro-border'}`} />
                    <span className="leading-snug">{isHi ? q.questionHi : q.questionEn}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* MASTER DYNAMIC SYNTHESIS CARD FOR SELECTED INQUIRY (Single Source of Truth) */}
          <div
            id="basic-selected-question-master-card"
            className="mt-4 p-5 sm:p-7 rounded-2xl bg-[#FAF6ED] border-2 border-astro-accent/40 shadow-xs space-y-5 animate-fade-in"
          >
            {/* Header with Inquiry Title, Category & Natal Promise Badge */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase bg-astro-primary text-white tracking-wider">
                    {isHi ? 'सक्रिय प्रश्न' : 'Selected Inquiry'}
                  </span>
                  <span className="text-xs font-bold text-astro-accent">
                    {activeThemeDisplay.name}
                  </span>
                  <span className="text-[10px] font-bold text-astro-secondary-text bg-astro-border/60 px-2 py-0.5 rounded-md uppercase">
                    {currentQuestion?.category || 'TIMING'}
                  </span>
                </div>
                <h4 className="text-base sm:text-xl font-bold font-serif text-astro-primary">
                  {currentQuestion ? (isHi ? currentQuestion.questionHi : currentQuestion.questionEn) : ''}
                </h4>
              </div>

              {/* Natal Promise Tag */}
              <span className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase shrink-0 self-start sm:self-auto ${promiseTier.badgeClass}`}>
                {isHi ? promiseTier.tierHi : promiseTier.tierEn}
              </span>
            </div>

            {/* Classical Synthesis Answer & Explanation */}
            <div className="space-y-2">
              <h5 className="text-sm sm:text-base font-bold font-serif text-astro-text">
                {isHi ? activeAnalysisResult.natalPromise.verdictHi : activeAnalysisResult.natalPromise.verdictEn}
              </h5>
              <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed">
                {isHi ? activeAnalysisResult.natalPromise.analysisSummaryHi : activeAnalysisResult.natalPromise.analysisSummaryEn}
              </p>
            </div>

            {/* Primary Supportive Fruition Timing Window */}
            <div className="p-4 sm:p-5 rounded-xl bg-astro-bg border border-astro-border space-y-3 shadow-2xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/80 pb-2.5">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-astro-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider text-astro-primary">
                    {isHi ? 'सर्वाधिक अनुकूल फल प्राप्ति समय सीमा' : 'Primary Supportive Timing Window'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {timingConfluence && (
                    <span className="text-[10px] font-bold text-astro-accent bg-[#FAF6ED] px-2 py-0.5 rounded-md border border-astro-border">
                      {timingConfluence.stars} {isHi ? timingConfluence.labelHi : timingConfluence.labelEn}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-baseline gap-3">
                <span className="text-2xl sm:text-3xl font-black text-astro-text font-mono tracking-tight">
                  {primaryWindow.startDate} — {primaryWindow.endDate}
                </span>
                {projectedAgeStr && (
                  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-astro-primary bg-[#FAF6ED] border border-astro-border px-2.5 py-0.5 rounded-md self-start shadow-2xs">
                    <Calendar className="w-3.5 h-3.5 text-astro-primary shrink-0" />
                    <span>{projectedAgeStr}</span>
                  </span>
                )}
              </div>

              <p className="text-xs sm:text-sm text-astro-text leading-relaxed font-serif">
                {isHi ? primaryWindow.astrologicalMechanismHi : primaryWindow.astrologicalMechanismEn}
              </p>

              <div className="pt-2.5 border-t border-astro-border/80 flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 text-astro-secondary-text">
                  <span>{isHi ? 'सक्रिय अनुकूल काल चक्र:' : 'Active Life Cycle (Dasha):'}</span>
                  <span className="font-bold font-mono text-astro-primary">
                    {primaryWindow.mahadashaLord}–{primaryWindow.antardashaLord}
                  </span>
                </div>
                <span className="text-[11px] text-astro-secondary-text">
                  {isHi ? 'शास्त्रीय देश-काल-पात्र अनुकूलता' : 'Classical Timing Harmony'}
                </span>
              </div>
            </div>

            {/* 4 Supportive Classical Pillars */}
            <div className="space-y-2.5 pt-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text block">
                {isHi ? 'यह समय आपके लिए क्यों अनुकूल है (४ शास्त्रीय आधार):' : 'Why This Phase Is Supportive (4 Classical Pillars):'}
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold text-astro-primary uppercase block">
                    1. {isHi ? 'सक्रिय जीवन क्षेत्र' : 'Focus Life Areas'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed font-medium">
                    {houseActivationText}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold text-astro-primary uppercase block">
                    2. {isHi ? 'मार्गदर्शक ग्रह बल' : 'Benefic Energies'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed font-medium">
                    {planetarySupportText}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold text-astro-primary uppercase block">
                    3. {isHi ? 'सक्रिय दशा चक्र' : 'Active Dasha'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed font-medium">
                    {dashaSupportText}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold text-astro-primary uppercase block">
                    4. {isHi ? 'गोचरीय संचार' : 'Harmonious Transits'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed font-medium">
                    {transitSupportText}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons Row */}
            <div className="pt-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-astro-border">
              <button
                type="button"
                onClick={() => {
                  const qText = currentQuestion ? (isHi ? currentQuestion.questionHi : currentQuestion.questionEn) : '';
                  setAiAstrologerInitialPrompt(qText);
                  setIsAIAstrologerOpen(true);
                }}
                className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-2xs w-full sm:w-auto justify-center"
              >
                <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                <span>{isHi ? 'इस प्रश्न पर एआई ज्योतिषी से चर्चा करें' : 'Discuss With AI Astrologer'}</span>
              </button>

              {onOpenAcharya && !isBasicUser && (
                <button
                  type="button"
                  onClick={() => {
                    const qText = currentQuestion ? (isHi ? currentQuestion.questionHi : currentQuestion.questionEn) : '';
                    onOpenAcharya(qText);
                  }}
                  className="px-3.5 py-2 rounded-xl bg-[#FAF6ED] hover:bg-astro-bg border border-astro-primary/40 text-astro-primary text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs w-full sm:w-auto justify-center"
                >
                  <Award className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'आचार्य परामर्श (Ask Acharya)' : 'Consult Ask Acharya'}</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setIsLifeQuestionsModalOpen(true)}
                className="text-xs font-bold text-astro-primary hover:text-astro-accent flex items-center gap-1 transition-colors cursor-pointer"
              >
                <span>{isHi ? 'पूर्ण ५०+ प्रश्न विवरण देखें' : 'View Full 50+ Questions Dossier'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Temporal Horizons Multi-Year Projection */}
          <TemporalWindowCard
            kundali={kundali}
            themeId={currentTheme.id}
            lang={lang}
          />
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 4. HARMONIZED VEDIC REMEDIES & DAILY WELLNESS HUB                          */}
      {/* ========================================================================= */}
      <section id="basic-harmonized-remedies-section" className="space-y-4">
        <HarmonizedRemediesHub
          kundali={kundali}
          analysisResult={activeAnalysisResult}
          selectedThemeId={currentTheme.id}
          lang={lang}
        />
      </section>

      {/* ========================================================================= */}
      {/* 5. LIFE QUESTIONS MODAL (50+ Curated Question Dossier)                     */}
      {/* ========================================================================= */}
      <BasicLifeQuestionsModal
        isOpen={isLifeQuestionsModalOpen}
        onClose={() => setIsLifeQuestionsModalOpen(false)}
        kundali={kundali}
        initialThemeId={currentTheme.id}
        initialQuestionId={currentQuestion?.id}
        lang={lang}
        onSelectThemeAndQuestion={(tId, qId) => {
          onSelectTheme(tId);
          onSelectQuestion(qId);
        }}
        onOpenDetailedEvidence={
          isBasicUser || !onSwitchRole
            ? undefined
            : () => {
                onSwitchRole('PRO');
              }
        }
        onOpenAcharya={isBasicUser ? undefined : onOpenAcharya}
      />

      {/* ========================================================================= */}
      {/* 6. FLOATING "ASK AI ASTROLOGER" WIDGET & DIALOG                           */}
      {/* ========================================================================= */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          id="btn-floating-ask-ai-astrologer"
          type="button"
          onClick={() => {
            setAiAstrologerInitialPrompt('');
            setIsAIAstrologerOpen(true);
          }}
          className="group relative flex items-center gap-2.5 px-4 sm:px-5 py-3 rounded-full bg-linear-to-r from-astro-primary to-astro-secondary text-white shadow-xl hover:shadow-2xl border-2 border-astro-accent/50 hover:border-astro-accent transition-all transform hover:-translate-y-0.5 cursor-pointer touch-manipulation"
          aria-label={isHi ? 'एआई ज्योतिषी से परामर्श लें' : 'Ask AI Astrologer'}
        >
          <div className="relative">
            <Sparkles className="w-5 h-5 text-astro-accent animate-pulse" />
            <span className="absolute -top-1 -right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-astro-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-astro-accent"></span>
            </span>
          </div>
          <span className="text-xs sm:text-sm font-bold tracking-wide font-serif">
            {isHi ? 'एआई ज्योतिषी से पूछें' : 'Ask AI Astrologer'}
          </span>
        </button>
      </div>

      {/* Floating AI Astrologer Modal Dialog */}
      {isAIAstrologerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-black/60 backdrop-blur-xs animate-fade-in"
          onClick={() => setIsAIAstrologerOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-astrologer-modal-title"
        >
          <div
            className="bg-astro-bg border border-astro-border rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#FAF6ED] border-b border-astro-border px-4 sm:px-6 py-3.5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-astro-primary text-white flex items-center justify-center shadow-xs">
                  <Sparkles className="w-5 h-5 text-astro-accent" />
                </div>
                <div>
                  <h3 id="ai-astrologer-modal-title" className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                    {isHi ? 'वैदिक एआई ज्योतिषी परामर्श' : 'Vedic AI Astrologer Consultation'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? 'अपनी जन्म कुंडली के आधार पर किसी भी विषय पर सीधा संवाद करें।'
                      : 'Direct conversational guidance powered by classical Vedic rules and your planetary positions.'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsAIAstrologerOpen(false)}
                className="w-8 h-8 rounded-full border border-astro-border bg-astro-bg hover:bg-astro-border flex items-center justify-center text-astro-secondary-text hover:text-astro-primary transition-colors cursor-pointer"
                aria-label={isHi ? 'बंद करें' : 'Close'}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body with GeminiAstrologer */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-5">
              <GeminiAstrologer
                kundali={kundali}
                lang={lang}
                initialPrompt={aiAstrologerInitialPrompt}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
